import type { CloudLibraryResponse, CloudPlaylistPayload, CloudTrackPayload } from "@needle-drop/shared";
import type { Playlist, Track } from "../model";

const TOKEN_KEY = "needle-drop-library-token";

export class CloudApiError extends Error {
  constructor(public readonly status: number, message: string) { super(message); }
}

export function getLibraryToken() { return localStorage.getItem(TOKEN_KEY) ?? ""; }
export function setLibraryToken(token: string) {
  const value = token.trim();
  if (value) localStorage.setItem(TOKEN_KEY, value); else localStorage.removeItem(TOKEN_KEY);
}

export function authHeaders(extra?: HeadersInit) {
  return new Headers({ ...Object.fromEntries(new Headers(extra).entries()), "X-Library-Token": getLibraryToken() });
}

export async function apiRequest(apiBaseUrl: string, path: string, init?: RequestInit) {
  const method = (init?.method ?? "GET").toUpperCase();
  const attempts = method === "GET" ? 2 : 1;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    let response: Response;
    try {
      response = await fetch(`${apiBaseUrl}${path}`, { ...init, headers: authHeaders(init?.headers) });
    } catch (error) {
      if (attempt + 1 < attempts) {
        await new Promise((resolve) => window.setTimeout(resolve, 350));
        continue;
      }
      throw new CloudApiError(0, error instanceof TypeError ? "The private cloud service could not be reached." : "Cloud request failed.");
    }
    if (response.ok) return response;
    if (attempt + 1 < attempts && [502, 503, 504].includes(response.status)) {
      await new Promise((resolve) => window.setTimeout(resolve, 350));
      continue;
    }
    const body = await response.json().catch(() => undefined) as { error?: { message?: string } } | undefined;
    throw new CloudApiError(response.status, body?.error?.message ?? `Cloud request failed (${response.status}).`);
  }
  throw new CloudApiError(0, "Cloud request failed.");
}

export async function fetchCloudLibrary(apiBaseUrl: string): Promise<CloudLibraryResponse> {
  const cacheBuster = encodeURIComponent(`${Date.now()}-${crypto.randomUUID()}`);
  return await (await apiRequest(apiBaseUrl, `/api/library?refresh=${cacheBuster}`, { cache: "no-store" })).json() as CloudLibraryResponse;
}

export function toCloudTrack(track: Track): CloudTrackPayload {
  return {
    id: track.id,
    title: track.title || track.fileName.replace(/\.mp3$/i, "") || "Untitled",
    artist: track.artist,
    album: track.album,
    ...(track.year ? { year: track.year } : {}),
    genres: track.genres,
    label: track.label,
    ...(track.bpm ? { bpm: track.bpm } : {}),
    musicalKey: track.musicalKey,
    rating: track.rating,
    ratingSource: track.ratingSource,
    playCount: track.playCount,
    favorite: track.favorite,
    tags: track.tags,
    categories: track.categories,
    duration: track.duration,
    fileName: track.fileName,
    fileSize: track.fileSize,
    fileHash: track.fileHash,
    ...(track.storageKey ? { storageKey: track.storageKey } : {}),
    ...(track.coverArtUrl ? { coverArtUrl: track.coverArtUrl } : {}),
    addedAt: track.addedAt,
    updatedAt: track.updatedAt,
    ...(track.isrc ? { isrc: track.isrc } : {}),
    ...(track.musicbrainzRecordingId ? { musicbrainzRecordingId: track.musicbrainzRecordingId } : {}),
    ...(track.musicbrainzReleaseId ? { musicbrainzReleaseId: track.musicbrainzReleaseId } : {}),
    provenance: track.provenance,
  };
}

export function fromCloudTrack(track: CloudTrackPayload): Track {
  return { ...track, storageMode: "r2", provenance: track.provenance as Track["provenance"] };
}

export function toCloudPlaylist(playlist: Playlist): CloudPlaylistPayload { return playlist; }

export async function saveCloudTrack(apiBaseUrl: string, track: Track) {
  await apiRequest(apiBaseUrl, `/api/tracks/${track.id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(toCloudTrack(track)),
  });
}

export async function saveCloudPlaylist(apiBaseUrl: string, playlist: Playlist) {
  await apiRequest(apiBaseUrl, `/api/playlists/${playlist.id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(toCloudPlaylist(playlist)),
  });
}

export async function uploadArtwork(apiBaseUrl: string, trackId: string, artwork: Blob) {
  await apiRequest(apiBaseUrl, `/api/artwork/${trackId}`, {
    method: "PUT",
    headers: { "Content-Type": artwork.type || "image/jpeg" },
    body: artwork,
  });
}

export async function getPrivateArtwork(apiBaseUrl: string, trackId: string) {
  return (await apiRequest(apiBaseUrl, `/api/artwork/${trackId}`)).blob();
}
